'use client'

import { Grid as Grid1, Autocomplete, Button, Typography, TextField } from "@mui/material"
import { useClient, useTranslation } from "@theme/base/contexts"
import { useFormikContext, useField } from "formik"
import { useEffect, useMemo, useState } from "react"
import { useRemote } from "@hooks"
import { useUtilityContext } from "@theme/base/contexts"
import { useUserProfile } from "@theme/base/contexts"
import { DeliveryNoteSearchRequestInputWithMonth } from "@theme/base/templates/DeliveryNotes/DeliveryNotes"
import { capitaliseFirstLetter } from '@helpers'
import { deliveryNotesSearchExport,  } from '@logics'
import { DeliveryNoteSearchExportRequestInput, TSeasonGetResponse } from '@schemas/graphql'
import moment from "moment"
import SearchFilterFormik from "../TableFilters/SearchFilterFormik"
import SelectFilterFormik from "../TableFilters/SelectFilterFormik"

const filtersToInputExport = (
  filters: DeliveryNoteSearchRequestInputWithMonth,
  seasons : TSeasonGetResponse[])
  : DeliveryNoteSearchExportRequestInput => {
    let result : DeliveryNoteSearchExportRequestInput = {
        order: '-deliveryDate',
        deliveryDateFrom: filters.month ? moment().subtract(parseInt(filters.month), 'months').toISOString() as any : undefined,
        search: filters.search,
        invoiced: filters.invoiced !== undefined ? filters.invoiced === 'true' : undefined,
        clientId: filters.clientId !== undefined ? filters.clientId : undefined,
    }
    if (filters.seasonId) {
        const season = seasons.find(s => s.id == filters.seasonId)
        result = {...result, createdAtFrom: season?.startDate ?? null, createdAtTo: season?.endDate ?? null}
    }
    return result
}

const DeliveryNotesTopFilters = ({ seasons } : { seasons: TSeasonGetResponse[] }) => {
    const { getTranslation } = useTranslation()
    const { values } = useFormikContext<DeliveryNoteSearchRequestInputWithMonth>()
    const { fetchRemote } = useRemote()
    const { downloadFile } = useUtilityContext()
    const { isSales,saleClients } = useUserProfile()
    const { client } = useClient()
    const [clientIdField, , clientHelpers] = useField('clientId')
    const { submitForm } = useFormikContext<DeliveryNoteSearchRequestInputWithMonth>()

    const clientOptions = saleClients?.map(client => ({
        value: client.clientId,
        label: capitaliseFirstLetter(client.name.toLowerCase())
    })).sort((a, b) => a.label.localeCompare(b.label)) ?? []

    const dateOptions = [
        { value: '1', label: getTranslation("common.filters.lastmonth") },
        { value: '3', label: getTranslation("common.filters.last3months") },
    ]

    const handleExport = () => {
        fetchRemote(async () => deliveryNotesSearchExport(filtersToInputExport(values, seasons))).then(response => {
            if (!response.fileName) return
            downloadFile(response.fileName.toString());
        })
    }

    const seasonsOptions = seasons.map((season) => ({
        value: season.id,
        label: season.name
    }))
    const selectedClientId = clientOptions.find(client => client.value === clientIdField.value)
    const clientOptionsWithAll = [
        { value: 'all', label: getTranslation("common.all") },
        ...clientOptions,
    ]
    return (
        <Grid1 container spacing={3} mb={6} alignItems='center'>
            <Grid1 item xs={12} lg={5.5}>
                <SearchFilterFormik paramName="search" />
            </Grid1>

            <Grid1 item xs={6} lg={1.5}>
                <SelectFilterFormik paramName="seasonId" labelTranslationKey={getTranslation('attributes.season')} options={seasonsOptions}/>
            </Grid1>

            <Grid1 item xs={6} lg={1.5}>
                <SelectFilterFormik paramName="month" labelTranslationKey={getTranslation("common.dates")} allOptionGender="feminine" options={dateOptions} />
            </Grid1>
            {isSales && <Grid1 item xs={12} lg={1.5}>
                <Autocomplete
                    disablePortal
                    options={clientOptionsWithAll}
                    fullWidth
                    size='small'
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    getOptionLabel={(option) => option.label}
                    getOptionKey={(option => option.value)}
                    value={selectedClientId}
                    renderInput={(params) => <TextField {...params} label={getTranslation("search.filter.client")} />}
                    onChange={(event, newValue) => {
                        clientHelpers.setValue(newValue?.value ?? '')
                        submitForm()
                    }}
                    disableClearable
                />
            </Grid1>}
            <Grid1 item xs={12} lg={2}>
                <Button size='large' onClick={handleExport} variant='contained' color='secondary' fullWidth>
                    <Typography variant='size16'>{getTranslation("common.exportlisting")}</Typography>
                </Button>
            </Grid1>
        </Grid1>
    )
}

export default DeliveryNotesTopFilters